import React, { useMemo } from "react";
import { codeClasses, PRIORITY_CODES } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Info, Lightning, Eraser } from "@phosphor-icons/react";

export default function DiscriminatorGrid({ protocol, selected, autoMatchedIds, onToggle, onClearAll, selectionCount }) {
  const autoIds = autoMatchedIds || new Set();
  const active = ((protocol && protocol.discriminators) || []).filter((d) => d.active !== false);
  const selectedIds = new Set(selected.map((s) => s.id));

  const groups = useMemo(() => PRIORITY_CODES.map((c) => {
    const list = active
      .filter((d) => d.code === c.code)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const selectedCount = list.filter((d) => selectedIds.has(d.id)).length;
    return { code: c, list, selectedCount };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [active, selected]);

  if (!protocol) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-slate-500">
        Seleziona un protocollo per visualizzare i discriminatori.
      </div>
    );
  }

  const toggleGroup = (list, allSelected) => {
    list.forEach((d) => {
      const isSel = selectedIds.has(d.id);
      if (autoIds.has(d.id)) return;
      if (allSelected && isSel) onToggle(d);
      else if (!allSelected && !isSel) onToggle(d);
    });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-lg font-semibold">Discriminatori</h3>
          <div className="text-xs text-slate-500">{protocol.name} · v.{protocol.version} · raggruppati per codice</div>
        </div>
        {selectionCount > 0 && onClearAll && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearAll}
            data-testid="deselect-all-button"
            className="min-h-[40px]"
          >
            <Eraser size={14} className="mr-2" /> Deseleziona tutto ({selectionCount})
          </Button>
        )}
      </div>

      {active.length === 0 ? (
        <div className="p-6 border-2 border-dashed border-slate-200 rounded-md text-center">
          <Info size={22} className="mx-auto text-slate-400 mb-2" />
          <p className="text-sm font-medium">Nessun discriminatore configurato per questo protocollo.</p>
          <p className="text-xs text-slate-500 mt-1">
            Aggiungerli dalla sezione <span className="font-medium">Protocolli</span> (Admin) o importare un file JSON.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map(({ code, list, selectedCount }) => {
            if (list.length === 0) return null;
            const total = list.length;
            const allSelected = selectedCount === total;
            const someSelected = selectedCount > 0 && selectedCount < total;

            return (
              <section
                key={code.code}
                data-testid={`disc-group-${code.code}`}
                className="rounded-md border border-slate-200 overflow-hidden"
              >
                <header
                  className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 cursor-pointer select-none"
                  onClick={() => toggleGroup(list, allSelected)}
                  data-testid={`disc-group-toggle-${code.code}`}
                >
                  <Checkbox
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    className="pointer-events-none"
                    data-testid={`disc-group-check-${code.code}`}
                  />
                  <span className={`chip-code text-sm ${code.classes}`}>{code.label}</span>
                  <div className="flex-1 text-xs text-slate-600">
                    <span className="font-semibold" data-testid={`disc-group-count-${code.code}`}>
                      {selectedCount}
                    </span>
                    <span className="text-slate-400"> / {total} discriminatori</span>
                    {selectedCount > 0 && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-500">
                        {allSelected ? "tutti selezionati" : "parziale"}
                      </span>
                    )}
                  </div>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 p-3">
                  {list.map((d) => {
                    const isSel = selectedIds.has(d.id);
                    const isAuto = autoIds.has(d.id);
                    return (
                      <label
                        key={d.id}
                        data-testid={`disc-${d.id}`}
                        className={`discriminator-card cursor-pointer flex items-start gap-3 p-3 rounded-md border ${
                          isAuto ? "border-slate-900 bg-slate-900/5 ring-1 ring-slate-900" :
                          isSel ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        <Checkbox
                          checked={isSel}
                          onCheckedChange={() => onToggle(d)}
                          disabled={isAuto}
                          className="mt-0.5"
                          data-testid={`disc-check-${d.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            {isAuto && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-slate-900 bg-yellow-200 px-1.5 py-0.5 rounded" data-testid={`disc-auto-${d.id}`}>
                                <Lightning size={10} weight="fill" /> Auto
                              </span>
                            )}
                            <span className="text-sm font-medium">{d.name}</span>
                          </div>
                          {d.description && <p className="text-xs text-slate-600">{d.description}</p>}
                          {d.hint && (
                            <p className="text-xs text-slate-500 mt-1 italic">Intervista: {d.hint}</p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
